// app/frontend/components/ui/Drawers/DrawerStatRow.tsx
//
// "12 upcoming events · 4 artists" under a drawer's title (#2203). A zero
// says nothing, so it isn't shown; with every count zero the row is gone.
// A stat with an href links to the full page's matching tab (#2442), so
// "8 artists" opens the city's Artists tab instead of sitting there as text.
import React from 'react';
import { Group } from '@mantine/core';
import { Link } from '@inertiajs/react';
import type { TablerIcon } from '@tabler/icons-react';
import { ThemedText } from '../../Typography';
import { COLORS } from '../../../src/constants/theme';
import { themeVar } from '../../../src/utils/pageTheme';

export interface DrawerStat {
  icon: TablerIcon;
  count: number;
  /** ['upcoming event', 'upcoming events'] */
  label: [string, string];
  /** The page tab that lists these, e.g. `/city/texas?active_tab=artists`. */
  href?: string;
}

// The underline marks the count as a link; it goes solid on hover. On touch
// screens the link grows to a 44px tap target.
const STYLES = `
  .wcwd-drawer-stat-link { color: inherit; text-decoration: underline dotted; text-decoration-color: ${themeVar('accent')}; text-underline-offset: 4px; }
  .wcwd-drawer-stat-link:hover { text-decoration-style: solid; }
  .wcwd-drawer-stat-link:focus-visible { outline: 2px solid ${themeVar('accent')}; outline-offset: 2px; border-radius: 2px; }
  @media (pointer: coarse) { .wcwd-drawer-stat-link { display: inline-flex; align-items: center; min-height: 44px; } }
`;

const DrawerStatRow: React.FC<{ stats: DrawerStat[] }> = ({ stats }) => {
  const shown = stats.filter((stat) => stat.count > 0);
  if (shown.length === 0) return null;
  return (
    <Group gap="md" wrap="wrap">
      {shown.map(({ icon: Icon, count, label, href }) => {
        const text = `${count} ${count === 1 ? label[0] : label[1]}`;
        return (
          <Group key={label[1]} gap={6} wrap="nowrap">
            <Icon size={16} color="currentColor" style={{ color: themeVar('accent') }} />
            <ThemedText tier="tertiary">
              {href ? (
                <Link className="wcwd-drawer-stat-link" href={href}>
                  {text}
                </Link>
              ) : (
                text
              )}
            </ThemedText>
          </Group>
        );
      })}
      {shown.some((stat) => stat.href) && <style>{STYLES}</style>}
    </Group>
  );
};

export default DrawerStatRow;
